import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, input, model } from '@angular/core';

export interface WhSegmentedOption<V = string> {
  /** Value emitted when the segment is picked. */
  value: V;
  /** Text label (status chips). Optional for icon-only segments. */
  label?: string;
  /** Glyph/emoji for icon-only segments (the list view-mode toggles). */
  icon?: string;
  /** Optional count badge on the right of the label (status chips). */
  count?: number;
  /** Native tooltip (icon-only segments use it for their accessible name). */
  title?: string;
}

/**
 * Canonical segmented control for both apps — the integrator toolbar's pill group: status filter
 * chips (label + count badge) and the table/cards view-mode toggle (icon-only). One `h-9 rounded-lg`
 * bordered strip; the active segment is `bg-gray-900 text-white`.
 *
 * Stateless like `lib-pager`: the parent owns the value. Bind `[value]` + `(valueChange)` (or the
 * two-way `[(value)]`). Not a form control — these are explicit filter/view toggles, not form fields.
 *
 *   <wh-segmented [options]="statusChips()" [value]="statusFilter()" (valueChange)="onStatusChip($event)" />
 *   <wh-segmented [options]="viewModes()" [value]="viewMode()" (valueChange)="setView($event)" [scroll]="false" />
 */
@Component({
  selector: 'wh-segmented',
  standalone: true,
  imports: [NgClass],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'inline-block' },
  template: `
    <div
      class="flex items-center gap-0.5 rounded-lg border border-gray-200 bg-white p-0.5 dark:border-slate-700 dark:bg-slate-800"
      [class.h-8]="size() === 'sm'"
      [class.h-9]="size() === 'default'"
      [class.overflow-x-auto]="scroll()">
      @for (o of options(); track o.value) {
        <button
          type="button"
          class="flex h-full shrink-0 items-center gap-1.5 rounded-md font-semibold transition-colors"
          [class.px-3]="size() === 'default'"
          [class.text-sm]="size() === 'default'"
          [class.px-2.5]="size() === 'sm'"
          [class.text-xs]="size() === 'sm'"
          [ngClass]="
            o.value === value()
              ? 'bg-gray-900 text-white dark:bg-slate-100 dark:text-slate-900'
              : 'text-gray-700 hover:bg-gray-100 dark:text-slate-300 dark:hover:bg-slate-700'
          "
          [title]="o.title ?? o.label ?? ''"
          (click)="pick(o.value)">
          @if (o.icon) {
            <span class="text-sm leading-none">{{ o.icon }}</span>
          }
          @if (o.label) {
            <span>{{ o.label }}</span>
          }
          @if (o.count !== undefined && o.count !== null) {
            <!-- Couleurs en classes (pas en style) : le segment actif est gray-900 en clair mais
                 slate-100 en sombre, un blanc en dur y devenait blanc sur blanc. -->
            <span
              data-testid="segment-count"
              class="rounded px-1.5 py-px text-xs font-bold"
              [ngClass]="
                o.value === value()
                  ? 'bg-white/20 text-white dark:bg-slate-900/10 dark:text-slate-900'
                  : 'bg-gray-100 text-gray-600 dark:bg-slate-700 dark:text-slate-200'
              ">
              {{ o.count }}
            </span>
          }
        </button>
      }
    </div>
  `,
})
export class WhSegmentedComponent<V = string> {
  /** Segments to render. */
  readonly options = input.required<readonly WhSegmentedOption<V>[]>();
  /** Selected value. Two-way `[(value)]`; `model` also exposes a `(valueChange)` output. */
  readonly value = model<V>();
  /** Allow the strip to scroll horizontally when segments overflow (status chips on mobile). */
  readonly scroll = input<boolean>(true);
  /** Trigger height. {@code 'sm'} = h-8 (32px) to line up with `xs` buttons and `sm`
   * selects in a toolbar; {@code 'default'} = h-9 (36px), the list-page filter height. */
  readonly size = input<'sm' | 'default'>('default');

  protected pick(v: V): void {
    this.value.set(v);
  }
}
